import React, { Suspense, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { DeferredContactModal } from "../components/DeferredContactModal";
import { Layout } from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";

// Modular studio components
import { HomeHero } from "../components/home/HomeHero";
import { HomeServiceGuide } from "../components/home/HomeServiceGuide";
const HomeAuditHighlight = React.lazy(() => import("../components/home/HomeAuditHighlight").then(m => ({ default: m.HomeAuditHighlight })));
import { DeferredSection, DeferredSectionWithNamespaces } from "../components/DeferredSection";
import { trackEvent } from "../lib/analytics";

// Below-the-fold sections are split out of the entry bundle. DeferredSection only
// controls *when* they mount; the dynamic import is what keeps their code (and the
// layout work it triggers) out of the initial render. anchorId is passed so the
// reservation still answers direct links to #manifesto / #services.
const HomeManifesto = React.lazy(() => import("../components/home/HomeManifesto").then(m => ({ default: m.HomeManifesto })));
const HomeServices = React.lazy(() => import("../components/home/HomeServices").then(m => ({ default: m.HomeServices })));
const HomeCaseStudies = React.lazy(() => import("../components/home/HomeCaseStudies").then(m => ({ default: m.HomeCaseStudies })));
const HomeTestimonials = React.lazy(() => import("../components/home/HomeTestimonials").then(m => ({ default: m.HomeTestimonials })));
const HomePricing = React.lazy(() => import("../components/home/HomePricing").then(m => ({ default: m.HomePricing })));
const HomeStudioAbout = React.lazy(() => import("../components/home/HomeStudioAbout").then(m => ({ default: m.HomeStudioAbout })));
const HomeFAQ = React.lazy(() => import("../components/home/HomeFAQ").then(m => ({ default: m.HomeFAQ })));
const HomeFinalCTA = React.lazy(() => import("../components/home/HomeFinalCTA").then(m => ({ default: m.HomeFinalCTA })));

// The below-the-fold home sections live in lazy chunks and read namespaces that
// are NOT in the inlined core. Declared here, in the file that mounts them, so
// the generator and tests/i18nSplit.test.ts can verify them against real usage.
const componentNamespaces = ["home_modern", "boost", "pricing", "cyber_rocket"];

export default function Home() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [contactOpen, setContactOpen] = useState(false);
  const [contactMessage, setContactMessage] = useState("");
  const [selectedPack, setSelectedPack] = useState<string>("Croissance");
  // NOTE : l'ancien dialogue "login requis" (loginAlertOpen/selectedPlan) a été
  // retiré — il n'était jamais ouvert (aucun setTrue dans le code).
  const [searchParams] = useSearchParams();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  // Auto-checkout if returning from login with checkout query param
  useEffect(() => {
    const checkoutPlan = searchParams.get('checkout');
    if (checkoutPlan && user) {
      window.history.replaceState({}, '', '/');
      const plan = checkoutPlan as 'Starter' | 'Business' | 'Premium' | 'Boost';
      handleCheckout(plan);
    }
  }, [user, searchParams]);

  // Handle smooth scroll if hash present in URL
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '');
      let attempts = 0;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      };
      setTimeout(tryScroll, 150);
    }
  }, []);

  const handleCheckout = async (planType: 'Starter' | 'Business' | 'Premium' | 'Boost') => {
    trackEvent('begin_checkout', { plan_type: planType, page: 'home' });
    const packMap: Record<string, string> = {
      Starter: 'Essentiel',
      Business: 'Croissance',
      Premium: 'Sur-Mesure',
      Boost: 'Croissance'
    };
    setSelectedPack(packMap[planType] || 'Croissance');

    if (planType === 'Premium') {
      setContactMessage(t('checkout.stripe_not_ready', { plan: planType }));
      setContactOpen(true);
      return;
    }

    try {
      setLoadingPlan(planType);
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ planType }),
      });

      const data = await response.json();

      if (data.error && data.error.includes("Stripe non configuré")) {
        setContactMessage(t('checkout.stripe_not_ready', { plan: planType }));
        setContactOpen(true);
        return;
      }

      if (data.error) {
        setContactMessage(t('checkout.error', { plan: planType }));
        setContactOpen(true);
        return;
      }

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setContactMessage(t('checkout.help', { plan: planType }));
      setContactOpen(true);
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <Layout>
      {/* Controlled Contact Modal */}
      <DeferredContactModal
        open={contactOpen} 
        onOpenChange={setContactOpen} 
        defaultMessage={contactMessage} 
        defaultPack={selectedPack}
      />

      {/* Main Page Sections */}
      <HomeHero />
      <HomeServiceGuide />
      {/* The lazy sections below live in the `home_modern` namespace, which is
          only partially inlined (the hero is). Each section therefore waits for
          its strings, exactly like a route does, so no section can ever paint a
          raw key like `home_modern.services.title`. */}
      <div className="section-divider" />
      <DeferredSectionWithNamespaces namespaces={componentNamespaces} anchorId="manifesto" reserveClassName="min-h-[46rem] md:min-h-[40rem]">
        <Suspense fallback={null}><HomeManifesto /></Suspense>
      </DeferredSectionWithNamespaces>
      <div className="section-divider" />
      {/* anchorId is safe alongside the section's own id="services": DeferredSection
          only sets the id on the placeholder and drops it once the real section
          mounts (isReady ? undefined : anchorId). That is what lets /#services deep
          links resolve before the chunk has loaded. */}
      <DeferredSectionWithNamespaces namespaces={componentNamespaces} anchorId="services" reserveClassName="min-h-[70rem] md:min-h-[62rem]">
        <Suspense fallback={null}><HomeServices /></Suspense>
      </DeferredSectionWithNamespaces>
      <div className="section-divider" />
      <DeferredSectionWithNamespaces namespaces={componentNamespaces} anchorId="audit-highlight" reserveClassName="min-h-[48rem]" rootMargin="300px 0px">
        <Suspense fallback={<div className="min-h-[48rem]" />}><HomeAuditHighlight /></Suspense>
      </DeferredSectionWithNamespaces>
      <div className="section-divider" />
      <DeferredSectionWithNamespaces namespaces={componentNamespaces} anchorId="portfolio" reserveClassName="min-h-[58rem] md:min-h-[70rem]">
        <Suspense fallback={null}>
          <HomeCaseStudies 
            onOpenConsultation={(category, title) => {
              setSelectedPack("Sur-Mesure");
              setContactMessage(`Bonjour Flash Web, j'ai découvert votre étude de cas "${title}" (${category}) et je souhaite concevoir un projet similaire pour mon activité.`);
              setContactOpen(true);
            }}
          />
        </Suspense>
      </DeferredSectionWithNamespaces>
      <div className="section-divider" />
      <DeferredSectionWithNamespaces namespaces={componentNamespaces} reserveClassName="min-h-[82rem] md:min-h-[76rem]">
        <Suspense fallback={null}><HomeTestimonials /></Suspense>
      </DeferredSectionWithNamespaces>
      <div className="section-divider" />
      <DeferredSectionWithNamespaces namespaces={componentNamespaces} anchorId="pricing" reserveClassName="min-h-[108rem] md:min-h-[84rem]">
        <Suspense fallback={null}>
          <HomePricing 
            loadingPlan={loadingPlan} 
            onCheckout={handleCheckout} 
            onOpenConsultation={(packName) => {
              setSelectedPack(packName);
              setContactMessage(`Bonjour, je souhaite échanger au sujet du Pack ${packName} pour mon site web.`);
              setContactOpen(true);
            }}
          />
        </Suspense>
      </DeferredSectionWithNamespaces>
      <div className="section-divider" />
      <DeferredSectionWithNamespaces namespaces={componentNamespaces} anchorId="studio" reserveClassName="min-h-[62rem] md:min-h-[56rem]">
        <Suspense fallback={null}><HomeStudioAbout /></Suspense>
      </DeferredSectionWithNamespaces>
      <div className="section-divider" />
      <DeferredSectionWithNamespaces namespaces={componentNamespaces} anchorId="faq" reserveClassName="min-h-[54rem] md:min-h-[48rem]">
        <Suspense fallback={null}><HomeFAQ /></Suspense>
      </DeferredSectionWithNamespaces>
      <div className="section-divider" />
      <DeferredSectionWithNamespaces namespaces={componentNamespaces} reserveClassName="min-h-[36rem]">
        <Suspense fallback={null}><HomeFinalCTA /></Suspense>
      </DeferredSectionWithNamespaces>
    </Layout>
  );
}
